import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtPrice } from "@/lib/totals";
import { Plus, Trash2, Pencil, Search, X, Package, Check, Tag, Link2 } from "lucide-react";
import { AddonI18nPanel } from "@/components/settings/addons/AddonI18nPanel";

/**
 * Iter 151 — Configuración de Add-ons v2.
 * Conectado a los catálogos reales del CRM (sólo IDs):
 *   Equipo/Modelo → Configuración → Catálogo de equipos
 *   Categoría     → Inventario → Categorías (derivada del producto)
 */
const inputCls = "mt-1 w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm bg-white";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-[#525252]";
const emptyRule = { part_id: "", part_name: "", part_sku: "", part_category: "", addon_price: 0, title: "", description: "", image_url: "", sort_order: 100, active: true };

export default function SettingsAddonsTab() {
  const [devices, setDevices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [equipmentId, setEquipmentId] = useState("");
  const [modelId, setModelId] = useState("");
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [i18nLang, setI18nLang] = useState("es-ES");
  const [i18nRefresh, setI18nRefresh] = useState(0);
  const [savingRule, setSavingRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState([]);
  const [showPartPicker, setShowPartPicker] = useState(false);
  const debPart = useRef(null);

  const loadCatalogs = async () => {
    try {
      const r = await api.get("/addons/catalogs");
      setDevices(r.data.devices || []);
      setCategories(r.data.categories || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const loadRules = async () => {
    if (!equipmentId || !modelId) { setRules([]); return; }
    try {
      const r = await api.get("/addons/rules", { params: { equipment_id: equipmentId, model_id: modelId } });
      setRules(r.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { loadCatalogs(); }, []);
  useEffect(() => { loadRules(); /* eslint-disable-next-line */ }, [equipmentId, modelId]);

  const equipment = useMemo(() => devices.find((d) => d.id === equipmentId), [devices, equipmentId]);
  const models = useMemo(() => (equipment?.models || []).filter((m) => m.active !== false), [equipment]);
  const modelName = models.find((m) => m.id === modelId)?.name || "";
  const categoryOfLabel = (label) =>
    categories.find((c) => (c.label || "").trim().toLowerCase() === (label || "").trim().toLowerCase());

  useEffect(() => {
    if (debPart.current) clearTimeout(debPart.current);
    if (!showPartPicker) return;
    debPart.current = setTimeout(async () => {
      try {
        const r = await api.get("/parts", { params: { q: partQuery } });
        setPartResults((r.data || []).filter((p) => p.active !== false).slice(0, 20));
      } catch { setPartResults([]); }
    }, 180);
  }, [partQuery, showPartPicker]);

  const openNewRule = () => {
    if (!equipmentId || !modelId) { toast.error("Selecciona Equipo y Modelo primero."); return; }
    setEditingRule(null);
    setI18nLang("es-ES");
    setRuleForm({ ...emptyRule, sort_order: (rules.length + 1) * 10 });
    setShowRuleDialog(true);
  };
  const openEditRule = (r) => {
    setEditingRule(r);
    setRuleForm({
      part_id: r.part_id, part_name: r.part_name || "", part_sku: r.part_sku || "", part_category: r.category_label || "",
      addon_price: r.addon_price || 0, title: r.title || "", description: r.description || "",
      image_url: r.image_url || "", sort_order: r.sort_order || 100, active: r.active !== false,
    });
    setI18nLang("es-ES");
    setShowRuleDialog(true);
  };
  const saveRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.part_id) { toast.error("Selecciona un producto/servicio"); return; }
    const payload = {
      equipment_id: equipmentId, model_id: modelId, part_id: ruleForm.part_id,
      addon_price: Number(ruleForm.addon_price) || 0,
      title: ruleForm.title, description: ruleForm.description, image_url: ruleForm.image_url,
      sort_order: parseInt(ruleForm.sort_order, 10) || 100, active: ruleForm.active !== false,
    };
    setSavingRule(true);
    try {
      if (editingRule) await api.put(`/addons/rules/${editingRule.id}`, payload);
      else await api.post("/addons/rules", payload);
      toast.success("Regla guardada · traducciones actualizadas");
      setShowRuleDialog(false);
      setI18nRefresh((n) => n + 1);
      loadRules();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSavingRule(false); }
  };
  const deleteRule = async (r) => {
    if (!window.confirm("¿Eliminar este Add-on?")) return;
    try { await api.delete(`/addons/rules/${r.id}`); toast.success("Eliminado"); loadRules(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleActive = async (r) => {
    try { await api.put(`/addons/rules/${r.id}`, { active: !r.active }); loadRules(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const moveRule = async (idx, dir) => {
    const arr = [...rules];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.patch("/addons/rules/reorder", { rule_ids: arr.map((r) => r.id) }); loadRules(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const formCategory = categoryOfLabel(ruleForm.part_category);

  return (
    <div className="space-y-6" data-testid="addons-tab">
      <section className="ds-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Package size={16} className="text-[#6E8B3D]" />
              <h3 className="font-display font-bold text-lg">Add-ons por equipo y modelo</h3>
            </div>
            <p className="text-xs text-[#525252] mt-1">
              Configura qué productos/servicios se ofrecerán al cliente para cada Equipo + Modelo.
              Las categorías ya presentes en las líneas del presupuesto se excluirán automáticamente.
            </p>
          </div>
          <div className="text-[10px] font-mono text-[#525252] flex flex-col gap-1 items-end" data-testid="addons-sources">
            <span className="inline-flex items-center gap-1"><Link2 size={10} /> Equipo/Modelo · Catálogo de equipos</span>
            <span className="inline-flex items-center gap-1"><Tag size={10} /> Categoría · Inventario ({categories.filter((c) => c.active !== false).length})</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
          <div>
            <label className={labelCls}>Equipo</label>
            <select value={equipmentId} onChange={(e) => { setEquipmentId(e.target.value); setModelId(""); }}
              data-testid="addon-device-select" className={inputCls}>
              <option value="">Selecciona equipo…</option>
              {devices.filter((d) => d.active !== false).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Modelo</label>
            <select value={modelId} onChange={(e) => setModelId(e.target.value)} disabled={!equipmentId}
              data-testid="addon-model-select" className={`${inputCls} disabled:opacity-50`}>
              <option value="">{equipmentId ? "Selecciona modelo…" : "Elige equipo primero"}</option>
              {models.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
            </select>
          </div>
        </div>

        {equipmentId && modelId && (
          <>
            <div className="mt-4 flex items-center justify-between">
              <div className={labelCls} data-testid="addon-rules-count">{rules.length} Add-on(s) configurados · orden ↕</div>
              <button onClick={openNewRule} data-testid="addon-rule-new" className="ds-btn primary">
                <Plus size={14} /> Añadir Add-on
              </button>
            </div>

            <div className="mt-3 rounded-xl border border-[#E5E5E0] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`${labelCls} border-b border-[#E5E5E0] text-left`}>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">Producto / Servicio</th>
                    <th className="px-3 py-2">Categoría</th>
                    <th className="px-3 py-2 text-right">Precio habitual</th>
                    <th className="px-3 py-2 text-right">Precio Add-on</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2 text-center">Activo</th>
                    <th className="px-3 py-2 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, i) => (
                    <tr key={r.id} className="border-b border-[#E5E5E0] last:border-0" data-testid={`addon-rule-${r.id}`}>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveRule(i, -1)} disabled={i === 0} className="text-[#525252] hover:text-[#111110] disabled:opacity-30" title="Subir">▲</button>
                          <button onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1} className="text-[#525252] hover:text-[#111110] disabled:opacity-30" title="Bajar">▼</button>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-sm">{r.part_name || <span className="text-red-600">Artículo eliminado</span>}</div>
                        <div className={labelCls}>{r.part_sku}{r.master_sku ? ` · maestro ${r.master_sku}` : ""}</div>
                        {r.title && <div className="text-[11px] text-[#3F5222] mt-0.5">"{r.title}"</div>}
                      </td>
                      <td className="px-3 py-2 text-xs" data-testid={`addon-rule-category-${r.id}`}>
                        {r.category_label ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${r.category_active ? "bg-[#F0F4E8] text-[#3F5222]" : "bg-neutral-100 text-neutral-500 line-through"}`}>
                            {r.category_label}
                          </span>
                        ) : (
                          <span className="text-[10px] text-red-600 font-mono">SIN CATEGORÍA</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{fmtPrice(r.regular_price)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs font-bold text-[#3F5222]">
                        {r.addon_price ? fmtPrice(r.effective_price ?? r.addon_price) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">{r.stock_label}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleActive(r)} data-testid={`addon-rule-toggle-${r.id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${r.active ? "bg-[#F0F4E8] text-[#3F5222]" : "bg-neutral-100 text-neutral-500"}`}>
                          {r.active ? <><Check size={10} /> Activo</> : "Inactivo"}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openEditRule(r)} className="p-1.5 hover:bg-[#F0F0EB] rounded" data-testid={`addon-rule-edit-${r.id}`}><Pencil size={13} /></button>
                        <button onClick={() => deleteRule(r)} className="p-1.5 hover:bg-red-50 text-red-700 rounded" data-testid={`addon-rule-delete-${r.id}`}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                  {rules.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-[#525252]">
                      No hay Add-ons configurados para {equipment?.name} · {modelName} todavía.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {showRuleDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRuleDialog(false)}>
          <div className="bg-white rounded-2xl border border-[#E5E5E0] w-full max-w-2xl max-h-[92vh] flex flex-col" style={{ boxShadow: "0 20px 50px rgba(15,18,16,.18)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0] rounded-t-2xl" style={{ background: "linear-gradient(180deg,#FDFDFB 0%,#FAFAF6 100%)" }}>
              <div className="font-display font-bold" data-testid="addon-rule-dialog-title">{editingRule ? "Editar Add-on" : "Nuevo Add-on"} · {equipment?.name} · {modelName}</div>
              <button onClick={() => setShowRuleDialog(false)} className="p-1.5 hover:bg-[#F0F0EB] rounded-lg"><X size={16} /></button>
            </div>
            <form onSubmit={saveRule} className="p-5 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className={labelCls}>Producto / Servicio *</label>
                {ruleForm.part_id ? (
                  <div className="mt-1 rounded-lg border border-[#E5E5E0] px-3 py-2 flex items-center gap-2 bg-[#F0F4E8]" data-testid="addon-part-selected">
                    <Check size={14} className="text-[#6E8B3D]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{ruleForm.part_name}</div>
                      <div className="font-mono text-[10px] uppercase text-[#525252]">{ruleForm.part_sku}</div>
                    </div>
                    <button type="button" onClick={() => setRuleForm({ ...ruleForm, part_id: "", part_name: "", part_sku: "", part_category: "" })} className="p-1 hover:bg-white rounded"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
                    <input value={partQuery} onChange={(e) => { setPartQuery(e.target.value); setShowPartPicker(true); }}
                      onFocus={() => setShowPartPicker(true)} placeholder="Buscar por SKU o nombre…"
                      data-testid="addon-part-search" className="mt-1 w-full pl-8 pr-3 py-2 rounded-lg border border-[#E5E5E0] text-sm" />
                    {showPartPicker && partResults.length > 0 && (
                      <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-auto rounded-lg border border-[#E5E5E0] bg-white z-10" style={{ boxShadow: "0 8px 20px rgba(15,18,16,.12)" }}>
                        {partResults.map((p) => (
                          <button key={p.id} type="button" data-testid={`addon-part-option-${p.id}`}
                            onClick={() => {
                              setRuleForm({ ...ruleForm, part_id: p.id, part_name: p.name, part_sku: p.sku, part_category: p.category || "" });
                              setShowPartPicker(false); setPartQuery("");
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-[#F0F4E8] flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{p.name}</div>
                              <div className="font-mono text-[10px] uppercase text-[#525252]">
                                {p.sku}{p.category ? ` · ${p.category}` : ""} · {p.kind === "service" ? "SERVICIO" : `Stock ${p.stock_effective ?? p.stock}`}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>Categoría (Inventario)</label>
                <div className={`${inputCls} bg-[#FAFAF6] text-[#525252] flex items-center gap-2`} data-testid="addon-rule-category">
                  <Tag size={12} />
                  {ruleForm.part_id
                    ? (formCategory ? formCategory.label : <span className="text-red-600">Sin categoría — asígnala en Inventario</span>)
                    : "Se asigna desde el producto seleccionado"}
                </div>
                <div className="mt-1 text-[10px] text-[#525252]">Heredada del artículo. Decide si el Add-on se excluye cuando el presupuesto ya contiene esa categoría.</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Precio Add-on (IVA inc.)</label>
                  <input type="number" step="0.01" value={ruleForm.addon_price} onChange={(e) => setRuleForm({ ...ruleForm, addon_price: e.target.value })}
                    data-testid="addon-rule-price" className={inputCls} />
                  <div className="mt-1 text-[10px] text-[#525252]">0 = usar precio habitual del inventario.</div>
                </div>
                <div>
                  <label className={labelCls}>Orden</label>
                  <input type="number" value={ruleForm.sort_order} onChange={(e) => setRuleForm({ ...ruleForm, sort_order: e.target.value })} className={inputCls} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={ruleForm.active} onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })} className="accent-[#6E8B3D]" />
                    Activo
                  </label>
                </div>
              </div>
              {(() => {
                const masterSlot = (
                  <>
                    <div>
                      <label className={labelCls}>Título comercial</label>
                      <input value={ruleForm.title} onChange={(e) => setRuleForm({ ...ruleForm, title: e.target.value })}
                        placeholder="Aprovecha para cambiar la batería" data-testid="addon-rule-title" className={inputCls} />
                    </div>
                    <div className="mt-3">
                      <label className={labelCls}>Descripción</label>
                      <textarea rows={3} value={ruleForm.description} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                        placeholder="Al tener el equipo en nuestro taller podemos realizar el cambio a un precio especial…" data-testid="addon-rule-description" className={inputCls} />
                    </div>
                    <p className="mt-1 text-[10px] text-[#525252]">ES es el contenido maestro: al guardar se actualizan las traducciones de los idiomas activos.</p>
                  </>
                );
                return editingRule ? (
                  <AddonI18nPanel ruleId={editingRule.id} lang={i18nLang} onLang={setI18nLang} masterSlot={masterSlot} inputCls={inputCls} labelCls={labelCls} refreshKey={i18nRefresh} />
                ) : masterSlot;
              })()}
              <div>
                <label className={labelCls}>Imagen (URL, opcional)</label>
                <input value={ruleForm.image_url} onChange={(e) => setRuleForm({ ...ruleForm, image_url: e.target.value })} className={`${inputCls} font-mono`} />
              </div>
              <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-3 bg-white border-t border-[#E5E5E0] flex items-center justify-end gap-2 rounded-b-2xl">
                <button type="button" onClick={() => setShowRuleDialog(false)} className="ds-btn">Cancelar</button>
                <button type="submit" disabled={savingRule} data-testid="addon-rule-save" className="ds-btn primary">{savingRule ? "Guardando y traduciendo…" : editingRule ? "Guardar" : "Añadir Add-on"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
